import type { DataProvider } from '@refinedev/core';
import { AxiosRequestConfig } from 'axios';
import BaseService from '../BaseService';
import appConfig from '@/configs/app.config';

const API_URL = appConfig.apiPrefix;

export const dataProvider: DataProvider = {
  getOne: async ({ resource, id, meta }) => {
    const param: AxiosRequestConfig = {
      url: `/${resource}/${id}`,
      method: 'GET',
    };
    const response = await BaseService(param);
    if (response.status < 200 || response.status > 299) throw response;
    const data = await response.data;
    return { data };
  },
  update: async ({ resource, id, variables }) => {
    const requiresFormData =
      resource === 'PurchaseRecords' ||
      resource === 'TransferRecords' ||
      resource === 'EmployeeCertifications' ||
      resource === 'TenantDetails';
    const hasFile = Object.values(variables as Record<string, unknown>).some(
      (value) => value instanceof File || value instanceof Blob
    );

    let data;
    let headers: { 'Content-Type'?: string } = { 'Content-Type': 'application/json' };

    if (hasFile || requiresFormData) {
      const formData = new FormData();
      for (const key in variables) {
        const value = variables[key];
        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(key, item));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value as any);
        }
      }
      data = formData;
      headers = {};
    } else {
      data = variables;
    }
    const param: AxiosRequestConfig = {
      url: `/${resource}`,
      method: 'POST',
      data,
      headers,
    };
    const response = await BaseService(param);
    if (response.status < 200 || response.status > 299) throw response;
    const responseData = await response.data;
    return { data: responseData };
  },
  getList: async ({ resource, filters, pagination }) => {
    const params = new URLSearchParams();
    let url = `/${resource}`;

    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if ('field' in filter && 'value' in filter) {
          if (filter.value !== '') {
            params.append(filter.field, filter.value);
          }
        }
      });
    }
    const paginatedResources = [
      'SupplierContractors',
      'SprayRecords',
      'Blocks',
      'PurchaseRecords',
      'TransferRecords',
      'Crops',
      'PlantSpacings',
      'Machines',
      'ChemicalDetails',
      'Employees',
      'Employees/inactive',
      'Sheds',
      'Stocktakes',
    ];
    const isPaginatedResource = paginatedResources.includes(resource);

    if (params.size != 0) {
      url += `?${params.toString()}`;
    }

    const param: AxiosRequestConfig = {
      url: url,
      method: 'GET',
    };
    const response = await BaseService(param);

    if (response.status < 200 || response.status > 299) throw response;
    const data = await response.data;
    if (isPaginatedResource) {
      return {
        data: data.data,
        total: data.totalRecords,
      };
    }
    return {
      data,
      total: data.length,
    };
  },

  create: async ({ resource, variables }) => {
    const requiresFormData =
      resource === 'PurchaseRecords' || resource === 'TransferRecords' || resource === 'TenantDetails';
    const hasFile = Object.values(variables as Record<string, unknown>).some(
      (value) => value instanceof File || value instanceof Blob
    );
    let data;
    let headers: { 'Content-Type'?: string } = { 'Content-Type': 'application/json' };
    if (hasFile || requiresFormData) {
      const formData = new FormData();
      for (const key in variables) {
        const value = variables[key];
        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(key, item));
        } else {
          formData.append(key, value as any);
        }
      }

      data = formData;
      headers = {};
    } else {
      data = variables;
    }
    const param: AxiosRequestConfig = {
      url: `/${resource}`,
      method: 'POST',
      data,
      headers,
    };
    const response = await BaseService(param);
    if (response.status < 200 || response.status > 299) throw response;
    const responseData = await response.data;
    return { data: responseData };
  },
  deleteOne: async ({ resource, id }) => {
    const param: AxiosRequestConfig = {
      url: `/${resource}/${id}`,
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    };
    const response = await BaseService(param);
    const data = await response.data;
    return { data };
  },
  getApiUrl: () => API_URL ?? '',
};
